// Locale registry shared by the Worker, the web app and build scripts.
// Adding a market (ja, ko…) = one entry here + a messages folder + content files;
// see docs/I18N.md. Traditional Chinese stays the source language and the unprefixed site.

export const LOCALES = [
  "zh-Hant",
  "zh-Hans",
  "en",
  "es",
  "fr",
  "pt-BR",
  "tr",
  "vi",
  "id",
  "it",
  "th",
  "uk",
  "nl",
] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = "zh-Hant";

/**
 * Light languages (D-231): only the free tool pages (/it/tools…) are translated. Everything else (app, help, pricing,
 * legal, features, articles) stays on the English site, missing UI strings fall back to English, and the teacher app
 * never switches to a light language. See docs/plans/2026-10-30-light-locales.md.
 */
export const LIGHT_LOCALES = ["it", "th", "uk", "nl"] as const;
export type LightLocale = (typeof LIGHT_LOCALES)[number];
export type FullLocale = Exclude<Locale, LightLocale>;
export const FULL_LOCALES: readonly FullLocale[] = LOCALES.filter((locale): locale is FullLocale => !(LIGHT_LOCALES as readonly string[]).includes(locale));
export const isLightLocale = (locale: Locale): locale is LightLocale => (LIGHT_LOCALES as readonly string[]).includes(locale);
/** The language whose full site (app, help, pricing…) serves a locale: itself, or English for a light language. */
export const siteLocale = (locale: Locale): FullLocale => isLightLocale(locale) ? "en" : locale;

export type LocaleConfig = {
  /** <html lang>, hreflang and JSON-LD inLanguage. */
  htmlLang: string;
  hreflang: string;
  schemaLanguage: string;
  ogLocale: string;
  /** Public marketing pages live under this prefix ("" for the default locale). */
  pathPrefix: "" | `/${string}`;
  /** BCP 47 tag handed to Intl (dates, numbers, plural rules). */
  intl: string;
  siteName: string;
  /** Native name shown in the language switcher. */
  label: string;
  /** Prices are USD everywhere for now; kept per locale so a market can change later. */
  currency: "USD";
  /** Used when the browser does not report a time zone. */
  defaultTimezone: string;
};

export const LOCALE_CONFIG: Record<Locale, LocaleConfig> = {
  "uk": { htmlLang: "uk", hreflang: "uk", schemaLanguage: "uk-UA", ogLocale: "uk_UA", pathPrefix: "/uk", intl: "uk-UA", siteName: "ClassMori", label: "Українська", currency: "USD", defaultTimezone: "Europe/Kyiv" },
  "nl": { htmlLang: "nl", hreflang: "nl", schemaLanguage: "nl-NL", ogLocale: "nl_NL", pathPrefix: "/nl", intl: "nl-NL", siteName: "ClassMori", label: "Nederlands", currency: "USD", defaultTimezone: "Europe/Amsterdam" },
  "th": { htmlLang: "th", hreflang: "th", schemaLanguage: "th-TH", ogLocale: "th_TH", pathPrefix: "/th", intl: "th-TH", siteName: "ClassMori", label: "ไทย", currency: "USD", defaultTimezone: "Asia/Bangkok" },
  "it": { htmlLang: "it", hreflang: "it", schemaLanguage: "it-IT", ogLocale: "it_IT", pathPrefix: "/it", intl: "it-IT", siteName: "ClassMori", label: "Italiano", currency: "USD", defaultTimezone: "Europe/Rome" },
  "pt-BR": { htmlLang: "pt-BR", hreflang: "pt-BR", schemaLanguage: "pt-BR", ogLocale: "pt_BR", pathPrefix: "/pt-br", intl: "pt-BR", siteName: "ClassMori", label: "Português (Brasil)", currency: "USD", defaultTimezone: "America/Sao_Paulo" },
  "tr": { htmlLang: "tr", hreflang: "tr", schemaLanguage: "tr-TR", ogLocale: "tr_TR", pathPrefix: "/tr", intl: "tr-TR", siteName: "ClassMori", label: "Türkçe", currency: "USD", defaultTimezone: "Europe/Istanbul" },
  "vi": { htmlLang: "vi", hreflang: "vi", schemaLanguage: "vi-VN", ogLocale: "vi_VN", pathPrefix: "/vi", intl: "vi-VN", siteName: "ClassMori", label: "Tiếng Việt", currency: "USD", defaultTimezone: "Asia/Ho_Chi_Minh" },
  "id": { htmlLang: "id", hreflang: "id", schemaLanguage: "id-ID", ogLocale: "id_ID", pathPrefix: "/id", intl: "id-ID", siteName: "ClassMori", label: "Bahasa Indonesia", currency: "USD", defaultTimezone: "Asia/Jakarta" },
  "zh-Hans": {
    htmlLang: "zh-Hans", hreflang: "zh-Hans", schemaLanguage: "zh-Hans-CN", ogLocale: "zh_CN", pathPrefix: "/zh-cn",
    // i18n-ignore
    intl: "zh-CN", siteName: "小森课堂", label: "简体中文", currency: "USD", defaultTimezone: "Asia/Shanghai",
  },
  "zh-Hant": {
    htmlLang: "zh-Hant", hreflang: "zh-Hant", schemaLanguage: "zh-Hant-TW", ogLocale: "zh_TW", pathPrefix: "",
    // i18n-ignore
    intl: "zh-TW", siteName: "小森課堂", label: "繁體中文", currency: "USD", defaultTimezone: "Asia/Taipei",
  },
  en: {
    htmlLang: "en", hreflang: "en", schemaLanguage: "en", ogLocale: "en_US", pathPrefix: "/en",
    intl: "en-US", siteName: "ClassMori", label: "English", currency: "USD", defaultTimezone: "America/New_York",
  },
  // Neutral Spanish for Spain and Latin America (docs/I18N.md glossary): hreflang "es" covers every
  // Spanish-speaking country; Intl uses es-ES (the largest market) for dates and numbers.
  es: {
    htmlLang: "es", hreflang: "es", schemaLanguage: "es", ogLocale: "es_ES", pathPrefix: "/es",
    intl: "es-ES", siteName: "ClassMori", label: "Español", currency: "USD", defaultTimezone: "Europe/Madrid",
  },
  // French for France, Belgium, Switzerland and Quebec (docs/I18N.md glossary, D-070).
  fr: {
    htmlLang: "fr", hreflang: "fr", schemaLanguage: "fr", ogLocale: "fr_FR", pathPrefix: "/fr",
    intl: "fr-FR", siteName: "ClassMori", label: "Français", currency: "USD", defaultTimezone: "Europe/Paris",
  },
};

/** Cookie shared by classmori.com and app.classmori.com (Domain=classmori.com). */
export const LOCALE_COOKIE = "classmori_locale";
/** Request header the web app sends so API errors come back in the page language. */
export const LOCALE_HEADER = "x-classmori-locale";
/** Query parameter that carries the language across hosts (?lang=en). */
export const LOCALE_QUERY = "lang";

export const isLocale = (value: unknown): value is Locale => typeof value === "string" && (LOCALES as readonly string[]).includes(value);

/** Maps loose tags (en-GB, zh-TW, zh-HK, zh) to a supported locale; null when unsupported. */
export function normalizeLocale(value: unknown): Locale | null {
  if (typeof value !== "string") return null;
  const tag = value.trim().toLowerCase();
  if (!tag) return null;
  if (tag === "pt" || tag.startsWith("pt-")) return "pt-BR";
  if (/^zh-(?:hans|cn|sg|my)(?:-|$)/.test(tag)) return "zh-Hans";
  if (tag === "zh" || tag.startsWith("zh-")) return "zh-Hant";
  const exact = LOCALES.find((locale) => locale.toLowerCase() === tag);
  if (exact) return exact;
  const base = tag.split("-")[0];
  return LOCALES.find((locale) => locale.toLowerCase() === base) ?? null;
}

/** Time zones where a bare "zh" browser means Simplified Chinese (D-205): mainland China, Singapore, Malaysia. */
const SIMPLIFIED_ZONES = ["Asia/Shanghai", "Asia/Chongqing", "Asia/Chungking", "Asia/Harbin", "Asia/Urumqi", "PRC", "Asia/Singapore", "Asia/Kuala_Lumpur", "Asia/Kuching"];

/**
 * Picks the first supported language from an Accept-Language header or navigator.languages. A bare "zh" (some Android
 * and Windows setups) says no script; with a time zone it follows the place, else it stays Traditional.
 */
export function localeFromLanguages(languages: readonly string[] | string | null | undefined, timezone?: string | null): Locale | null {
  const list = typeof languages === "string"
    ? languages.split(",").map((part) => part.split(";")[0])
    : languages ?? [];
  for (const language of list) {
    if (language.trim().toLowerCase() === "zh" && timezone && SIMPLIFIED_ZONES.includes(timezone)) return "zh-Hans";
    const locale = normalizeLocale(language);
    // A light language is only reachable by its own URL; a browser set to Italian gets the English app.
    if (locale) return siteLocale(locale);
  }
  return null;
}

/** Splits "/en/pricing" into { locale: "en", basePath: "/pricing" }; unprefixed paths are the default locale. */
export function localeFromPath(path: string): { locale: Locale; basePath: string; prefixed: boolean } {
  for (const locale of LOCALES) {
    const prefix = LOCALE_CONFIG[locale].pathPrefix;
    if (!prefix) continue;
    if (path === prefix || path === `${prefix}/`) return { locale, basePath: "/", prefixed: true };
    if (path.startsWith(`${prefix}/`)) return { locale, basePath: path.slice(prefix.length), prefixed: true };
  }
  return { locale: DEFAULT_LOCALE, basePath: path, prefixed: false };
}

/** Public page path in a locale: localizePath("/pricing", "en") → "/en/pricing"; "/" → "/en". */
export function localizePath(basePath: string, locale: Locale): string {
  const prefix = LOCALE_CONFIG[locale].pathPrefix;
  if (!prefix) return basePath;
  if (basePath === "/" || basePath === "") return prefix;
  if (basePath.startsWith("/?") || basePath.startsWith("/#")) return `${prefix}${basePath.slice(1)}`;
  return `${prefix}${basePath}`;
}

export function readLocaleCookie(cookieHeader: string | null | undefined): Locale | null {
  if (!cookieHeader) return null;
  for (const part of cookieHeader.split(";")) {
    const [name, ...rest] = part.trim().split("=");
    if (name === LOCALE_COOKIE) return normalizeLocale(decodeURIComponent(rest.join("=")));
  }
  return null;
}
